import * as React from 'react';
import { Layout, Spin, Alert, Typography, Button, Space } from 'antd';
import { LogoutOutlined } from '@ant-design/icons';
import { getDocument } from '../firebase/firestore';
import { signOutUser, getCurrentUser } from '../firebase/auth';
import { ShopActions } from './ShopActions';

const { Content, Header } = Layout;
const { Title, Text } = Typography;

interface Shop {
    id: string;
    url: string;
    adminEmails?: string[];
    scope?: string;
    source?: string;
    syncProductsFromComax?: boolean;
    generateComaxInvoices?: boolean;
    name?: string;
    email?: string;
    comaxOnlineCustomerId?: string;
    comaxOnlineStoreId?: string;
    comaxToShopifyLocations?: Record<string, number>;
    comaxStoreIds?: string;
    shippingItemBarcode?: string;
    discountItemBarcode?: string;
    shopifyToken?: string;
    comaxUsername?: string;
    comaxPassword?: string;
    isActive?: boolean;
    adjustInventoryBy?: number;
    returnShippingFee?: number;
    [key: string]: any;
}

interface ShopViewProps {
    shopUrl: string;
}

export function ShopView({ shopUrl }: ShopViewProps): React.ReactElement {
    const [shop, setShop] = React.useState<Shop | null>(null);
    const [loading, setLoading] = React.useState<boolean>(true);
    const [error, setError] = React.useState<string | null>(null);
    const [signingOut, setSigningOut] = React.useState<boolean>(false);
    const [user] = React.useState(getCurrentUser());
    const [isMobile, setIsMobile] = React.useState<boolean>(window.innerWidth < 768);

    React.useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    React.useEffect(() => {
        async function fetchShop() {
            try {
                setLoading(true);
                setError(null);
                // Use shopUrl as the document ID
                const shopData = await getDocument<Shop>('shops', shopUrl);
                if (!shopData) {
                    setError('Shop not found');
                    return;
                }
                setShop(shopData);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to fetch shop');
                console.error('Error fetching shop:', err);
            } finally {
                setLoading(false);
            }
        }

        fetchShop();
    }, [shopUrl]);

    const handleSignOut = async () => {
        try {
            setSigningOut(true);
            await signOutUser();
        } catch (err) {
            console.error('Error signing out:', err);
        } finally {
            setSigningOut(false);
        }
    };


    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <Spin size="large" tip="Loading shop..." />
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ padding: isMobile ? '16px' : '24px' }}>
                <Alert message="Error" description={error} type="error" showIcon />
            </div>
        );
    }

    if (!shop) {
        return (
            <div style={{ padding: isMobile ? '16px' : '24px' }}>
                <Alert message="Shop Not Found" description="The requested shop could not be found." type="error" showIcon />
            </div>
        );
    }

    return (
        <Layout style={{ height: '100vh', width: '100vw' }}>
            <Header style={{ 
                background: '#fff', 
                padding: isMobile ? '0 16px' : '0 24px', 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                borderBottom: '1px solid #f0f0f0'
            }}>
                <Title level={isMobile ? 5 : 4} style={{ margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: isMobile ? '60%' : 'none' }}>
                    {shop.name || shop.url}
                </Title>
                <Space>
                    {!isMobile && (
                        <Text type="secondary" style={{ fontSize: '14px' }}>{user?.email}</Text>
                    )}
                    <Button 
                        icon={<LogoutOutlined />} 
                        onClick={handleSignOut}
                        loading={signingOut}
                        size={isMobile ? 'small' : 'middle'}
                    >
                        {isMobile ? '' : 'Sign Out'}
                    </Button>
                </Space>
            </Header>
            <Content style={{ 
                padding: isMobile ? '16px' : '24px', 
                background: '#fff', 
                width: '100%', 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                minHeight: 'calc(100vh - 64px)'
            }}>
                <ShopActions shop={shop} />
            </Content>
        </Layout>
    );
}

